import { useDroppable } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Box, Stack } from '@mui/material'
import { CarrierVisitStatus } from '@planning/app/api'
import { useMinimalsTheme } from '@tom-ui/ui'
import { FC } from 'react'
import {
  extractRailcarPlanningNumberIdentifierByType,
  isRailcarPlanningIdentifierOfType,
  RailcarPlanningDraggableEnum,
  RailcarPlanningViewStore,
} from '../../Stores/RailcarPlanningViewStore'
import { RailcarPlanningDroppableArea } from '../Molecules/RailcarPlanningDroppableArea'
import { RailcarPlanningRailcarProps } from '../Molecules/RailcarPlanningRailcar'
import { RailcarPlanningDraggableContainer } from './RailcarPlanningDraggableContainer'
import { RailcarPlanningDraggableRailcar } from './RailcarPlanningDraggableRailcar'

export type RailcarPlanningDroppableRailcarProps = {
  railcarPlanningViewStore: RailcarPlanningViewStore
  railcar: RailcarPlanningRailcarProps
  filter?: string | null
}

export const RailcarPlanningDroppableRailcar: FC<RailcarPlanningDroppableRailcarProps> = props => {
  const { railcar, filter, railcarPlanningViewStore } = props
  const theme = useMinimalsTheme()

  const sortable = useSortable({
    id: `${RailcarPlanningDraggableEnum.RAILCAR}:${railcar.id}`,
    disabled: railcarPlanningViewStore.railVisitItem.data.status === CarrierVisitStatus.Closed,
    data: {
      type: RailcarPlanningDraggableEnum.RAILCAR,
      ...railcar,
    },
  })
  const droppable = useDroppable({
    id: `${RailcarPlanningDraggableEnum.RAILCAR}:${railcar.id}`,
    disabled: railcarPlanningViewStore.railVisitItem.data.status === CarrierVisitStatus.Closed,
    data: {
      type: RailcarPlanningDraggableEnum.RAILCAR,
      ...railcar,
    },
  })

  // Check if the currently dragged container is already assigned to this railcar
  const isSameRailcar =
    !!droppable.active?.id &&
    railcar.containers?.some(
      c =>
        c.id ===
        extractRailcarPlanningNumberIdentifierByType(
          droppable.active!.id,
          RailcarPlanningDraggableEnum.CONTAINER,
        ),
    )

  const isOverContainerInRailcar =
    !!droppable.over?.id &&
    isRailcarPlanningIdentifierOfType(droppable.over.id, RailcarPlanningDraggableEnum.CONTAINER) &&
    railcar.containers?.some(
      container =>
        container.id ===
        extractRailcarPlanningNumberIdentifierByType(
          droppable.over!.id,
          RailcarPlanningDraggableEnum.CONTAINER,
        ),
    )

  const isDragginContainer =
    !!droppable.active?.id &&
    isRailcarPlanningIdentifierOfType(droppable.active.id, RailcarPlanningDraggableEnum.CONTAINER)

  const allowMovement = !droppable.active?.data?.current?.isGap

  return (
    <Stack
      ref={node => {
        sortable.setNodeRef(node)
        droppable.setNodeRef(node)
      }}
      {...sortable.attributes}
      {...sortable.listeners}
      style={{
        transform: allowMovement ? CSS.Transform.toString(sortable.transform) : 'none',
        transition: 'transform 200ms ease',
      }}
    >
      <Stack
        sx={{ height: '145px ', width: '100%' }}
        spacing={theme.customSpacing.xxs}
        direction='row'
      >
        {railcar.containers?.map(container => (
          <Stack key={container.id}>
            <RailcarPlanningDraggableContainer
              railcarPlanningViewStore={railcarPlanningViewStore}
              container={container}
              filter={filter}
              widthBasedOnContainerSize
            />
          </Stack>
        ))}
        <RailcarPlanningDroppableArea
          railcar={railcar}
          isOver={!!(droppable.isOver || isOverContainerInRailcar)}
          isSameRailcar={!!isSameRailcar}
          isDragginContainer={isDragginContainer}
        />
      </Stack>
      <Box mt={2}>
        <RailcarPlanningDraggableRailcar
          railcarPlanningViewStore={props.railcarPlanningViewStore}
          railcar={railcar}
          filter={filter}
          sequence={sortable.index}
        />
      </Box>
    </Stack>
  )
}
